// React & Hooks

import AirDatepicker, {
  type AirDatepickerLocale,
  type AirDatepickerOptions,
  type AirDatepickerPosition,
} from 'air-datepicker';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// External Library and Internalization
import { useTranslation } from 'react-i18next';
import type { CellComponent, RowComponent, Tabulator } from 'tabulator-tables';
import useSensorManagement from '@/domains/asset-management/hooks/useSensorManagement';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
// UI Component
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import { ReactTabulator } from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
// Custom Hooks (business logic and data management)
import { remoteSortHandler, useServerTable } from '@/shared/hooks/useServerTable';
import useSiteManagement from '@/shared/hooks/useSiteManagement';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';

import 'air-datepicker/air-datepicker.css';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import dayjs from 'dayjs';
import { number } from 'mathjs';
import type {
  CreateSensorPayload,
  SensorDetailData,
  SensorFormValues,
  UpdateSensorPayload,
} from '@/domains/asset-management/types/sensor';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

const commonColumnProps = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false as const,
};

// Tabulator column field -> backend allowed-sort whitelist key.
// air-sensor endpoint allows: id (air_sensor_id) | updated_time.
const AIR_SENSOR_SORT_FIELD_MAP: Record<string, string> = {
  air_sensor_id: 'id',
  updated_time: 'updated_time',
};

// (columns are built in-component so the "No" formatter can use the server page/size)

const EnvironmentSensor = () => {
  const { t, i18n } = useTranslation();

  const tbRef = useRef<Tabulator | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [disabledForm, setDisabledForm] = useState<boolean>(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const isNewClickedRef = useRef(isNewClicked);
  const [newId, setNewId] = useState<number | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<null | string>(null);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  //Params
  const table = useServerTable<{ search?: string }>({
    defaultSort: { field: 'updated_time', dir: 'desc' },
    defaultSize: 10,
  });
  const queryParams = table.queryString;
  interface SelectedSensor {
    air_sensor_id: number | null;
  }

  // Kemudian di component:
  const [selectedSensor, setSelectedSensor] = useState<SelectedSensor>({
    air_sensor_id: null,
  });
  const selectedSensorRef = useRef(selectedSensor);
  const detailSensor = useCallback((data: SensorDetailData) => {
    return {
      air_sensor_id: data.air_sensor_id || null,
      config_json: data.config_json || null,
      description: data.description || null,
      installed_date: data.installed_date || null,
      ip: data.ip || null,
      lat: data.lat || null,
      lng: data.lng || null,
      manager_name: data.manager_name || null,
      manager_phone: data.manager_phone || null,
      name: data.name || null,
      sensor_type: data.sensor_type || null,
      serial_no: data.serial_no || null,
      site_id: data.site_id || null,
    };
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));

      if (name === 'site_id') {
        setSelectedSiteId(value);
      }

      if (isNewClickedRef.current) {
        setHasChangesCreate(true);
      } else {
        setHasChangesUpdate(true);
      }

      if (selectedSensorRef.current?.air_sensor_id) {
        setHasChangesUpdate(true);
      }
    },
    []
  );

  useEffect(() => {
    let locale: AirDatepickerLocale;
    if (i18n.language === 'eng') {
      locale = localeEn;
    } else if (i18n.language === 'ind') {
      locale = localeId;
    } else {
      locale = localeKo;
    }

    const optionsDate: AirDatepickerOptions<HTMLElement> = {
      autoClose: true,
      locale: locale,
      position: 'top center' as AirDatepickerPosition,
      onSelect: (date) => {
        const selected = Array.isArray(date.formattedDate)
          ? date.formattedDate[0]
          : date.formattedDate;
        handleInputChange({
          target: {
            name: 'installed_date',
            value: dayjs(selected).format('YYYY-MM-DD'),
          },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
        setFormValues((prevValues) => ({
          ...prevValues,
          installed_date: dayjs(selected).format('YYYY-MM-DD'),
        }));
      },
    };

    const datepicker = new AirDatepicker('[name="installed_date"]', optionsDate);

    return () => {
      datepicker.destroy();
    };
  }, [i18n.language, handleInputChange]);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    isNewClickedRef.current = isNewClicked;
    selectedSensorRef.current = selectedSensor;
  }, [hasChangesUpdate, hasChangesCreate, isNewClicked, selectedSensor]);

  // values
  const [formValues, setFormValues] = useState<SensorFormValues>({
    air_sensor_id: null,
    site_id: null,
    name: null,
    sensor_type: null,
    serial_no: null,
    lat: null,
    lng: null,
    installed_date: null,
    manager_name: null,
    manager_phone: null,
    ip: null,
    description: null,
    config_json: null,
  });

  const emptyDetail = () => {
    setFormValues((prevValues) => {
      const emptyValues = {} as SensorFormValues;

      (Object.keys(prevValues) as (keyof SensorFormValues)[]).forEach((key) => {
        emptyValues[key] = null;
      });
      return emptyValues;
    });
  };

  //Button
  const [buttonState, setButtonState] = useState({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = useCallback(() => {
    setButtonState({
      confirm: true,
      cancel: true,
      delete: true,
      create: true,
    });
  }, []);

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prevState) => ({
      ...prevState,
      create: false,
    }));
  };

  const enableUPDATEButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
      delete: false,
      create: false,
    }));
  }, [disableAllButtons]);

  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
    }));
  };

  const reloadCallback = () => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setHasChangesUpdate(false);
    setIsNewClicked(false);
    setHasChangesCreate(false);
    setSelectedSiteId(null);
    // setSelectedDetector({ dt_id: null });
    setSelectedSensor({ air_sensor_id: null });
    setNewId(null);
    // Table refresh is handled by React Query cache invalidation in useSensorManagement.
  };

  useEffect(() => {
    if (!newId || !tbRef.current) return;
    const checkAndSelect = () => {
      try {
        const row = tbRef.current!.getRow(newId) || null;
        if (row && typeof row.select === 'function') {
          row.select();
          row.getElement().classList.add('custom-hover');
          setSelectedSensor({ air_sensor_id: newId });
          setDisabledForm(false);
          setNewId(null);
          enableUPDATEButtons();
        } else {
          setTimeout(checkAndSelect, 100);
        }
      } catch {
        setTimeout(checkAndSelect, 100);
      }
    };

    checkAndSelect();
  }, [newId, enableUPDATEButtons]);

  const {
    sensorListData,
    detailSensorData,
    createSensor,
    deleteSensor,
    updateSensor,
    refetchDetailSensor,
  } = useSensorManagement({
    airSensorID: selectedSensor?.air_sensor_id,
    queryParams: queryParams,
    onDeleteSuccess: reloadCallback,
    onUpdateSuccess: () => {
      // Table refresh is handled by RQ cache invalidation; re-select the edited row.
      refetchDetailSensor();
      const sensorId = selectedSensorRef.current?.air_sensor_id;
      if (sensorId) {
        setNewId(sensorId);
      }
      enableUPDATEButtons();
      setIsNewClicked(false);
      setHasChangesUpdate(false);
    },
    onCreateSuccess: (responseData) => {
      reloadCallback();
      setNewId(responseData.air_sensor_id);
    },
  });
  const pageData = sensorListData?.data;
  const data = pageData?.rows ?? [];
  const dataSensor = detailSensorData?.data;
  const sensorColumns = useMemo(
    () => [
      {
        title: 'No',
        field: 'air_sensor_id',
        formatter: (cell: CellComponent) =>
          (table.page - 1) * table.size + cell.getRow().getPosition(true),
        width: 60,
        headerSort: false,
        ...commonColumnProps,
      },
      // TODO: i18n - hardcoded Korean string
      {
        title: 'ID',
        field: 'air_sensor_id',
        widthGrow: 0.7,
        headerSort: true,
        ...commonColumnProps,
      },
      {
        title: '명칭',
        field: 'name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '시리얼 넘버',
        field: 'serial_no',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '타입',
        field: 'sensor_type_value',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '담당자',
        field: 'manager_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '담당자 전화번호',
        field: 'manager_phone',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '업데이트 일시',
        field: 'updated_time',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnProps,
      },
    ],
    [table.page, table.size]
  );
  const { siteListData } = useSiteManagement({
    queryParams: '',
    siteId: null,
  });
  const dataSite = siteListData?.data;

  const languageTabulator = useCallback(() => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'),
        first_title: t('cmn > first page'),
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  }, [t]);

  const placeholder = "<div style='text-align:center;'> No Data</div>";
  const optionsTabulator = useMemo(
    () => ({
      debugInvalidOptions: true,
      // Server-side pagination: Tabulator's own pager is off; <TablePagination> drives it.
      pagination: false,
      rowHeight: 41,
      selectableRows: false,
      movableRows: false,
      index: 'air_sensor_id',
      resizableRows: false,
      locale: 'ko',
      langs: {
        ko: languageTabulator(),
      },
      placeholder,
    }),
    [languageTabulator]
  );

  const handleSearch = useCallback(
    (inputVal: string) => {
      const keyword = inputVal.trim();
      table.setFilters({ search: keyword || undefined });
    },
    [table]
  );

  const handleReset = () => {
    table.reset();
  };

  const handleRowSelected = useCallback(
    (row: RowComponent) => {
      if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message
          .confirmClicked()
          .then(() => {
            tbRef.current?.deselectRow();
            row.select();
            row.getElement().classList.add('custom-hover');

            const rowData = row.getData();
            setSelectedSensor({
              air_sensor_id: rowData.air_sensor_id,
            });
            setSelectedSiteId(rowData.site_id ?? 'NO_MAPPING');
            setDisabledForm(false);
            setHasChangesUpdate(false);
            setHasChangesCreate(false);
            setIsNewClicked(false);
          })
          .catch(() => {
            row.deselect();

            const prevId = selectedSensorRef.current?.air_sensor_id;
            if (prevId) {
              const prevRow = tbRef.current?.getRow(prevId) || null;
              prevRow?.select();
              prevRow?.getElement().classList.add('custom-hover');
            }
          });
      } else {
        tbRef.current?.deselectRow();
        row.select();
        row.getElement().classList.add('custom-hover');

        const rowData = row.getData();
        setSelectedSensor({
          air_sensor_id: rowData.air_sensor_id,
        });
        setSelectedSiteId(rowData.site_id ?? 'NO_MAPPING');
        setDisabledForm(false);
        enableUPDATEButtons();
        setIsNewClicked(false);
      }
      requestAnimationFrame(() => {
        if (nameRef.current) {
          nameRef.current.focus();
        }
      });
    },
    [enableUPDATEButtons, t]
  );

  const handleNewButtonClick = () => {
    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(true);
        enableRegisterButtons();
        setSelectedSensor({ air_sensor_id: null });
        setSelectedSiteId(null);
        setDisabledForm(false);
        setHasChangesUpdate(false);
      });
    } else {
      tbRef.current?.deselectRow();
      emptyDetail();
      setIsNewClicked(true);
      enableRegisterButtons();
      setSelectedSensor({ air_sensor_id: null });
      setSelectedSiteId(null);
      setDisabledForm(false);
      setHasChangesUpdate(false);
    }
    requestAnimationFrame(() => {
      if (nameRef.current) {
        nameRef.current.focus();
      }
    });
  };

  const handleCancelButtonClick = () => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        enableInitialButtons();
        setHasChangesUpdate(false);
        setDisabledForm(true);
        setHasChangesUpdate(false);
        setSelectedSiteId(null);
        // setSelectedSensor({ dt_id: null });
        setSelectedSensor({ air_sensor_id: null });
        // ----------------------------------------------------------

        requestAnimationFrame(() => {
          if (nameRef.current) {
            nameRef.current.focus();
          }
        });
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message.confirmClicked().then(() => {
          emptyDetail();
          setIsNewClicked(false);
          setDisabledForm(true);
          enableInitialButtons();
          setHasChangesCreate(false);
          setSelectedSiteId(null);
        });
      } else {
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        enableInitialButtons();
        setHasChangesCreate(false);
        setSelectedSiteId(null);
      }
    } else {
      reloadCallback();
    }
  };

  // Add Strict Validation -> Already to defines values notice message with multiple language on PostgreSQL databases.
  const validateSensorForm = (): boolean => {
    const {
      name,
      serial_no,
      lat,
      lng,
      installed_date,
      manager_name,
      manager_phone,
      ip,
      description,
      config_json,
    } = formValues;

    // Required fields
    if (
      !name?.trim() ||
      lat == null ||
      lng == null ||
      !installed_date?.trim() ||
      !manager_name?.trim() ||
      !manager_phone?.trim() ||
      !serial_no?.trim() ||
      !ip?.trim() ||
      String(lat).trim() === '' ||
      String(lng).trim() === ''
    ) {
      new NoticeMessage(t('msg > required values'));
      return false;
    }
    // Name validation
    if (name.length > 100) {
      new NoticeMessage(t('msg > sensor name max length'));
      return false;
    }
    // Latitude validation
    if (isNaN(Number(lat))) {
      new NoticeMessage(t('msg > invalid latitude'));
      return false;
    }
    if (String(lat).length > 100) {
      new NoticeMessage(t('msg > latitude max length'));
      return false;
    }
    // Longitude validation
    if (isNaN(Number(lng))) {
      new NoticeMessage(t('msg > invalid longitude'));
      return false;
    }
    if (String(lng).length > 100) {
      new NoticeMessage(t('msg > longitude max length'));
      return false;
    }
    // Installed date validation
    if (!dayjs(installed_date, 'YYYY-MM-DD', true).isValid()) {
      new NoticeMessage(t('msg > invalid install date'));
      return false;
    }
    // Serial number validation
    if (serial_no.length > 100) {
      new NoticeMessage(t('msg > serial max length'));
      return false;
    }
    // Manager validation
    if (manager_name.length > 100) {
      new NoticeMessage(t('msg > manager max length'));
      return false;
    }
    // Phone numeric validation
    if (!/^[0-9]+$/.test(manager_phone)) {
      new NoticeMessage(t('msg > invalid phone'));
      return false;
    }
    // IP validation
    const ipRegex =
      /^(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;

    if (!ipRegex.test(ip)) {
      new NoticeMessage(t('msg > invalid ip'));
      return false;
    }
    // Description validation
    if (description && description.length > 500) {
      new NoticeMessage(t('msg > description max length'));
      return false;
    }
    // JSON length validation
    if (config_json && config_json.length > 10000) {
      new NoticeMessage(t('msg > json max length'));
      return false;
    }
    // JSON validation
    if (config_json) {
      try {
        JSON.parse(config_json);
      } catch {
        new NoticeMessage(t('msg > invalid json'));
        return false;
      }
    }
    return true;
  };

  const handleRegistButtonClick = () => {
    if (!validateSensorForm()) return;

    const payload: CreateSensorPayload = {
      site_id:
        !formValues.site_id || formValues.site_id === 'NO_MAPPING'
          ? null
          : number(formValues.site_id),
      name: formValues.name ?? '',
      description: formValues.description ?? '',
      sensor_type: formValues.sensor_type ?? '',
      serial_no: formValues.serial_no ?? '',
      lat: Number(formValues.lat),
      lng: Number(formValues.lng),
      installed_date: formValues.installed_date ?? '',
      manager_name: formValues.manager_name ?? '',
      manager_phone: formValues.manager_phone ?? '',
      ip: formValues.ip ?? '',
      config_json: formValues.config_json ?? '',
    };
    createSensor(payload);
  };

  const handleConfirmButtonClick = () => {
    if (!validateSensorForm()) return;

    const payload: UpdateSensorPayload = {
      site_id:
        !formValues.site_id || formValues.site_id === 'NO_MAPPING'
          ? null
          : number(formValues.site_id),
      name: formValues.name ?? '',
      description: formValues.description ?? '',
      sensor_type: formValues.sensor_type ?? '',
      serial_no: formValues.serial_no ?? '',
      lat: Number(formValues.lat),
      lng: Number(formValues.lng),
      installed_date: formValues.installed_date ?? '',
      manager_name: formValues.manager_name ?? '',
      manager_phone: formValues.manager_phone ?? '',
      ip: formValues.ip ?? '',
      config_json: formValues.config_json ?? '',
      air_sensor_id: Number(formValues.air_sensor_id),
    };

    updateSensor({
      sensorData: payload,
      params: {
        air_sensor_id: Number(payload.air_sensor_id),
      },
    });
  };

  const handleDeleteButtonClick = () => {
    if (dataSensor?.site_id) {
      new NoticeMessage('해당 함체에 매핑된 사이트가 존재합니다. 먼저 매핑을 해제해주세요');
      return;
    }

    const message = new NoticeMessage(t('msg > delete confirm'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      deleteSensor(selectedSensor?.air_sensor_id ?? '');
      if (refetchDetailSensor) {
        refetchDetailSensor();
      }
    });
  };

  useEffect(() => {
    if (dataSensor) {
      const data = detailSensor(dataSensor);
      setFormValues(data);
    }
  }, [dataSensor, detailSensor]);
  const logData = dataSensor
    ? [
        { label: t('cmn > registered by'), value: dataSensor.registered_by },
        {
          label: t('cmn > registered time'),
          value: dataSensor.registered_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataSensor.registered_time))
            : dataSensor.registered_time,
        },
        { label: t('cmn > updated by'), value: dataSensor.updated_by },
        {
          label: t('cmn > updated time'),
          value: dataSensor.updated_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataSensor.updated_time))
            : dataSensor.updated_time,
        },
      ]
    : [];

  return (
    <section className="wrap">
      <div className="header-title">
        {/* TODO: i18n - hardcoded Korean string */}
        <h3>센서 관리</h3>
        <h3>&gt;</h3>
        <h3>환경 센서 속성 관리</h3>
      </div>

      <ContainerCard>
        {/* TODO: i18n - hardcoded Korean string */}
        <Filtering
          searchRef={searchRef}
          // labelSelect="매핑 사이트 타입"
          placeholder="명칭 / 시리얼 넘버"
          disableFiltering={true}
          onReset={handleReset}
          onSearch={handleSearch}
        ></Filtering>
      </ContainerCard>

      <ContainerCard>
        <div className="table-container relative">
          <ReactTabulator
            id="detector-management-table"
            data={data}
            columns={sensorColumns}
            layout={'fitColumns'}
            className="tabulator-custom w-full "
            onRef={(r) => {
              tbRef.current = r.current;
            }}
            options={optionsTabulator}
            events={{
              rowClick: (_e: UIEvent, row: RowComponent) => handleRowSelected(row),
              // Header click → delegate sorting to the server (see useServerTable).
              dataSorted: remoteSortHandler(table, AIR_SENSOR_SORT_FIELD_MAP),
              tableBuilt: () => {
                if (!tbRef.current) return;
                if (selectedSensor?.air_sensor_id) {
                  try {
                    const row = tbRef.current.getRow(selectedSensor.air_sensor_id) || null;
                    if (row && typeof row.select === 'function') {
                      row.select();
                      row.getElement().classList.add('custom-hover');
                    }
                  } catch {
                    /* row not found */
                  }
                } else if (newId) {
                  try {
                    const row = tbRef.current.getRow(newId) || null;
                    if (row && typeof row.select === 'function') {
                      tbRef.current.scrollToRow(row, 'bottom', true);
                      row.select();
                      row.getElement().classList.add('custom-hover');
                      handleRowSelected(row);
                    }
                  } catch {
                    /* row not found */
                  }
                }
              },
            }}
          />

          {/* Server-side Pagination Controls - Tabulator style */}
          <div className="tabulator-footer">
            <div className="tabulator-paginator">
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(1)}
                disabled={table.page === 1}
              >
                {t('cmn > first page')}
              </button>
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(table.page - 1)}
                disabled={table.page === 1}
              >
                {t('cmn > page before')}
              </button>
              <span className="tabulator-pages">
                {Array.from({ length: Math.min(5, pageData?.total_pages ?? 0) }, (_, i) => {
                  const totalPages = pageData?.total_pages ?? 0;
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (table.page <= 3) {
                    pageNum = i + 1;
                  } else if (table.page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = table.page - 2 + i;
                  }
                  return (
                    <button
                      type="button"
                      key={pageNum}
                      className={`tabulator-page ${table.page === pageNum ? 'active' : ''}`}
                      onClick={() => table.setPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </span>
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(table.page + 1)}
                disabled={table.page === (pageData?.total_pages ?? 0)}
              >
                {t('cmn > next page')}
              </button>
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(pageData?.total_pages ?? 1)}
                disabled={table.page === (pageData?.total_pages ?? 0)}
              >
                {t('cmn > last page')}
              </button>
            </div>
            <div style={{ padding: '0 20px 0 0', textAlign: 'right' }}>
              총 {pageData?.total ?? 0} 건
            </div>
          </div>
        </div>
      </ContainerCard>

      <ContainerCard className=" flex flex-col">
        <div className="box-management-col flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-[50px]">
            {/* TODO: i18n - hardcoded Korean string */}
            <DetailForm
              className="items-center!"
              label="환경 센서 ID"
              value={formValues.air_sensor_id || ''}
              name={'air_sensor_id'}
              disabled={true}
              onChange={handleInputChange}
            />

            <DetailForm
              className="items-center!"
              label="명칭"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.name || ''}
              name={'name'}
              maxLength={100}
              formRef={nameRef}
            />

            <DetailForm
              inputType={'select'}
              className="items-center!"
              label="타입"
              optionSelect={[
                { value: 'AIR_TP_OUT', label: '옥외형 대기질' },
                { value: 'AIR_TP_IN', label: '실내형 공기질' },
              ]}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.sensor_type || ''}
              name="sensor_type"
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <div className="flex w-full flex-row gap-x-4">
              <DetailForm
                className="items-center!"
                label="설치 위/경도"
                required={true}
                showInput={false}
              >
                <div className="flex w-full flex-row gap-x-2">
                  <GeneralInput
                    customInput="w-full"
                    disabled={disabledForm}
                    onChange={handleInputChange}
                    value={formValues.lat || ''}
                    name={'lat'}
                    maxLength={100}
                    pattern={'^[0-9.]*$'}
                  />

                  <GeneralInput
                    customInput="w-full"
                    disabled={disabledForm}
                    onChange={handleInputChange}
                    value={formValues.lng || ''}
                    name={'lng'}
                    maxLength={100}
                    pattern={'^[0-9.]*$'}
                  />
                </div>
              </DetailForm>
            </div>

            <DetailForm
              className="items-center!"
              label="설치 일시"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.installed_date || ''}
              name={'installed_date'}
              readonly={true}
            />

            {/* contact person */}
            <DetailForm
              className="items-center!"
              label="담당자"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.manager_name || ''}
              name={'manager_name'}
              maxLength={100}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="담당자 전화번호"
              required={true}
              value={formValues.manager_phone || ''}
              name={'manager_phone'}
              disabled={disabledForm}
              onChange={handleInputChange}
              maxLength={100}
              pattern={'^[0-9]*$'}
            />

            <DetailForm
              className="items-center!"
              label="시리얼 넘버"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.serial_no || ''}
              name={'serial_no'}
              maxLength={100}
            />

            <DetailForm
              className="items-center!"
              label="IP"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.ip || ''}
              name={'ip'}
              maxLength={100}
              pattern={'^[0-9.]*$'}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              inputType="select"
              className="items-center!"
              label="매핑 사이트"
              optionSelect={[
                { value: 'NO_MAPPING', label: '매핑 없음' },
                ...(dataSite?.map((site) => ({
                  value: site.site_id.toString(),
                  label: `${site.name} (${site.site_id})`,
                })) || []),
              ]}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.site_id || ''}
              name={'site_id'}
            />
          </div>

          <div className="grid grid-cols-1">
            <DetailForm
              className="h-[90px] [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
              inputType="textarea"
              label="설명"
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.description || ''}
              name={'description'}
              maxLength={500}
            />
          </div>
          <div className="grid grid-cols-1">
            <DetailForm
              className="h-[150px] [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
              inputType="textarea"
              label="json 속성 (dev)"
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.config_json || ''}
              name="config_json"
              maxLength={10000}
            />
          </div>

          <hr className="border-t border-gray-300" />
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex-1">{disabledForm ? null : <LogList logs={logData} />}</div>
            <div className="flex-none">
              <ButtonGroup
                onClickDelete={handleDeleteButtonClick}
                onClickNew={handleNewButtonClick}
                onClickCancel={handleCancelButtonClick}
                onClickRegist={handleRegistButtonClick}
                onClickConfirm={handleConfirmButtonClick}
                isNewClicked={isNewClicked}
                cancelButtonState={buttonState.cancel}
                confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                deleteButtonState={buttonState.delete}
                newButtonState={hasChangesCreate ? false : buttonState.create}
              />
            </div>
          </div>
        </div>
      </ContainerCard>
    </section>
  );
};

export default EnvironmentSensor;
